import { createBrowserRouter } from 'react-router';

import { ROUTES } from 'pages/shared/routes';
import { TENANT_ID_URL_PARAM } from 'pages/shared/components/GlobalAutoShownOverlayContent/SwitchTenantModal/extras';

export type Router = ReturnType<typeof createBrowserRouter>;

export function patchRouterForAddingTenantIdInUrl<
  T extends Pick<Router, 'navigate'>,
>(router: T) {
  const patchedRouter = new Proxy(router, {
    get(target, prop, receiver) {
      if (prop === 'navigate') {
        return patchMethodForAddingTenantIdInUrl(target[prop]);
      }

      return Reflect.get(target, prop, receiver);
    },
  });

  return patchedRouter;
}

function patchMethodForAddingTenantIdInUrl(
  originalMethod: Router['navigate'],
): typeof originalMethod {
  return function methodWithAddedTenantIdInUrl(
    // https://www.typescriptlang.org/docs/handbook/2/functions.html#declaring-this-in-a-function
    this: Router | undefined,
    ...args
  ) {
    const currentUrlTenantId = new URLSearchParams(window.location.search).get(
      TENANT_ID_URL_PARAM,
    );

    // if string path is passed to push
    if (
      typeof args[0] === 'string' &&
      !(
        args[0].includes(ROUTES.login) || args[0].includes(ROUTES.userSuspended)
      )
    ) {
      const path = args[0];
      const [pathUrl, pathSearch] = path.split('?');
      const pathUrlParams = new URLSearchParams(pathSearch);
      const pathTenantId = pathUrlParams.get(TENANT_ID_URL_PARAM);

      // if path doesn't already include tenantId, append current tenantId to path
      if (currentUrlTenantId && !pathTenantId) {
        pathUrlParams.set(TENANT_ID_URL_PARAM, currentUrlTenantId);
        const newPath = `${pathUrl}?${pathUrlParams.toString()}`;
        args.splice(0, 1, newPath);
      }
    } else if (
      typeof args[0] !== 'string' &&
      typeof args[0] !== 'number' &&
      args[0] !== null &&
      !(
        args[0].pathname?.includes(ROUTES.login) ||
        args[0].pathname?.includes(ROUTES.userSuspended)
      )
    ) {
      const location = args[0];
      const locationUrlParams = new URLSearchParams(location.search);
      const locationTenantId = locationUrlParams.get(TENANT_ID_URL_PARAM);

      // if location doesn't already include tenantId, append current tenantId to location's search
      if (currentUrlTenantId && !locationTenantId) {
        locationUrlParams.set(TENANT_ID_URL_PARAM, currentUrlTenantId);
        const newLocation: typeof location = {
          ...location,
          search: locationUrlParams.toString(),
        };
        args.splice(0, 1, newLocation);
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return originalMethod.apply(this, args as any);
  };
}
